import React, { useMemo } from "react";
import { ThemeProvider } from "@mui/material";
import { MemoryRouter } from "react-router-dom";
import chromeTheme from "../../constants/chromeTheme";
import {
  SnackAlertProvider,
  SnackAlert,
} from "../../contexts/SnackAlertContext";
import { NavProvider } from "../../contexts/NavContext";
import PAGE_HEADERS from "./registry";

// Rails renders the same server-computed appProps blob (permissions + school +
// year/term scope) into every legacy page as `<script id="app-props">`; the v1
// chrome bundle this runs in already relies on it for the navbar. Reading it here
// lets a bridge header resolve its tabs from the navigation registry (useNavMenu)
// with the exact same permission gating the nav menu applies - no v1 permission
// plumbing and no new endpoint.
function readAppProps() {
  const el = document.getElementById("app-props");
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || "null");
  } catch {
    return null;
  }
}

// The single construct every legacy page header renders through. It resolves a
// definition by key and wraps it in the minimal provider stack a PageHeader
// needs, mirroring the v1 chrome's navbar/footer roots:
//   - chromeTheme: px-based sizing so the header and its portaled menus are not
//     shrunk by the legacy <html> font scaling.
//   - MemoryRouter: satisfies PageHeader's useNavigate(). It is inert here - real
//     navigation flows through the definition's onNavigate callback into the v1
//     router, never through this in-memory history.
//   - SnackAlertProvider: any action that calls useSnackAlert works.
// No FullscreenPageProvider: useFullscreenPage() returns null, so no back arrow,
// which is correct (the legacy navbar is always present above this header).
export default function PageHeaderChrome({ headerKey, params }) {
  const appProps = useMemo(readAppProps, []);
  const Definition = PAGE_HEADERS[headerKey];
  // Unknown key: render nothing rather than crash the legacy page.
  if (!Definition) return null;

  // Registry-driven headers (useNavMenu) navigate through this: a v1 route stays
  // in the legacy SPA via the page's own navigate (params.onNavigate, which may
  // carry the unsaved-changes guard); a v2 route is a hard nav across the app
  // boundary. The scope (year/term) the legacy page owns is passed in params.
  const bridgeNavigate = (url, v1) => {
    if (!url) return;
    if (v1 && params.onNavigate) params.onNavigate(url);
    else window.location.assign(url);
  };

  return (
    <ThemeProvider theme={chromeTheme}>
      <MemoryRouter>
        <SnackAlertProvider>
          <NavProvider
            appProps={appProps}
            schoolSlug={appProps?.school?.slug}
            schoolYearId={params.schoolYearId ?? null}
            navigationMode="browser"
            navigate={bridgeNavigate}
          >
            <Definition {...params} />
            <SnackAlert />
          </NavProvider>
        </SnackAlertProvider>
      </MemoryRouter>
    </ThemeProvider>
  );
}
